import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Table, Tag, Statistic, Button, Typography, message, Popconfirm, Modal, Checkbox, Alert, Space, Tooltip, Input, Form, InputNumber, Select } from 'antd';
import { ArrowLeftOutlined, RollbackOutlined, WhatsAppOutlined, DeleteOutlined, LoadingOutlined, SyncOutlined, EditOutlined, UserAddOutlined, PlusOutlined, CheckCircleOutlined } from '@ant-design/icons';
import api from '../../../../services/api';
import dayjs from 'dayjs';
import { getActiveTeamId } from '../../../../utils/activeTeam';
import { grossUp, round2 } from '../../../../utils/mpFees';

const { Text, Title } = Typography;
const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

const ACCESS_REASON_MSG = {
  not_in_beta: 'Módulo financeiro ainda não liberado pro seu time.',
  no_credits: 'Sem créditos. Compra mais créditos no card.',
  suspended: 'Módulo suspenso pra esse time. Fala com o suporte.',
  mp_disconnected: 'Conta Mercado Pago desconectada. Reconecta na tela financeiro.',
};

export default function BatchDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const teamId = getActiveTeamId();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [remindOpen, setRemindOpen] = useState(false);
  const [remindTarget, setRemindTarget] = useState(null); // null = bulk (batch todo), número = charge única
  const [remindChannels, setRemindChannels] = useState({ email: true, whatsapp: false });
  const [remindLoading, setRemindLoading] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get(`/community/finance/charges/batches/${id}`, { params: { team_id: teamId } });
      setData(r.data);
    } catch (e) { if (!silent) message.error(e.response?.data?.message || 'Falhou carregar'); }
    finally { if (!silent) setLoading(false); }
  }, [id, teamId]);
  useEffect(() => { load(); }, [load]);

  // Auto-refresh silencioso só quando o worker ainda está processando.
  // Estados finais (sent, partial_error, error) NÃO disparam polling — mesmo
  // que existam charges pending sem mp_preference_id (essas são falhas do
  // envio que ficam pra excluir manualmente, não pra esperar).
  const batchStatus = data?.batch?.status;
  const isProcessing = batchStatus === 'scheduled' || batchStatus === 'sending';
  useEffect(() => {
    if (!isProcessing) return;
    const it = setInterval(() => load(true), 3000);
    return () => clearInterval(it);
  }, [isProcessing, load]);

  const refund = async (chargeId) => {
    try {
      await api.post(`/community/finance/charges/${chargeId}/refund`, { community_team_id: teamId });
      message.success('Estorno solicitado · vai ser confirmado em segundos');
      setTimeout(load, 3000);
    } catch (e) { message.error(e.response?.data?.message || 'Falhou'); }
  };

  // Marcar como pago manualmente (dinheiro/pix externo/etc)
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markPaidCharge, setMarkPaidCharge] = useState(null);
  const [markPaidForm] = Form.useForm();
  const [markPaidSaving, setMarkPaidSaving] = useState(false);
  const openMarkPaid = (charge) => {
    setMarkPaidCharge(charge);
    markPaidForm.setFieldsValue({
      paid_amount_brl: Number(charge.amount_brl),
      paid_method: 'cash',
      note: '',
    });
    setMarkPaidOpen(true);
  };
  const executeMarkPaid = async () => {
    try {
      const v = await markPaidForm.validateFields();
      setMarkPaidSaving(true);
      await api.patch(`/community/finance/charges/${markPaidCharge.id}/mark-paid`, {
        team_id: teamId,
        paid_amount_brl: v.paid_amount_brl,
        paid_method: v.paid_method,
        note: v.note || null,
      });
      message.success('Cobrança marcada como paga');
      setMarkPaidOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || 'Falhou marcar como pago');
    } finally { setMarkPaidSaving(false); }
  };

  const deleteCharge = async (chargeId) => {
    try {
      const r = await api.delete(`/community/finance/charges/${chargeId}`, { params: { team_id: teamId } });
      if (r.data.batch_removed) {
        message.success('Cobrança excluída. Como era a única do lote, o lote também foi removido.');
        setTimeout(() => nav('/comunidade/financeiro'), 1500);
      } else {
        message.success('Cobrança excluída.');
        load();
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou excluir cobrança');
    }
  };

  const deleteBatch = async () => {
    try {
      const r = await api.delete(`/community/finance/charges/batches/${id}`, { params: { team_id: teamId } });
      message.success(`Cobrança excluída (${r.data.deleted} destinatário(s) removido(s)).`);
      setTimeout(() => nav('/comunidade/financeiro'), 1200);
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou excluir cobrança');
    }
  };

  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();
  // Edição de UMA cobrança individual (nome/email/telefone/valor).
  const [editChargeOpen, setEditChargeOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);
  const [editChargeForm] = Form.useForm();
  const [editChargeSaving, setEditChargeSaving] = useState(false);
  const openEditCharge = (charge) => {
    setEditingCharge(charge);
    editChargeForm.setFieldsValue({
      recipient_name: charge.recipient_name || '',
      recipient_email: charge.recipient_email || '',
      recipient_phone: charge.recipient_phone || '',
      amount_brl: Number(charge.amount_brl || 0),
    });
    setEditChargeOpen(true);
  };
  const saveEditCharge = async () => {
    try {
      const v = await editChargeForm.validateFields();
      setEditChargeSaving(true);
      // Se link já foi gerado, valor não vai — não manda amount_brl no body.
      const body = { team_id: teamId, recipient_name: v.recipient_name,
        recipient_email: v.recipient_email || '', recipient_phone: v.recipient_phone || '' };
      if (!editingCharge?.mp_preference_id && Number(v.amount_brl) > 0) {
        body.amount_brl = Number(v.amount_brl);
      }
      await api.put(`/community/finance/charges/${editingCharge.id}`, body);
      message.success('Cobrança atualizada');
      setEditChargeOpen(false);
      setEditingCharge(null);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || 'Falhou atualizar');
    } finally { setEditChargeSaving(false); }
  };
  const [addOpen, setAddOpen] = useState(false);
  const [addRecipients, setAddRecipients] = useState([]);
  const [addAmount, setAddAmount] = useState(50);
  const [addSaving, setAddSaving] = useState(false);
  // absorbFee: dono paga a taxa MP e cobra do jogador o valor bruto pra
  // receber exatamente o líquido escolhido. Simetria com o wizard novo.
  const [addAbsorbFee, setAddAbsorbFee] = useState(false);
  // Assume PIX (0,99% - mais comum). Se pagador escolher cartão (4,99%),
  // time recebe menos — aviso mostrado na UI.
  const addAssumedMethods = { pix: true };
  const addManualRow = () => setAddRecipients(r => [...r, { name: '', email: '', phone: '', amount_brl: addAmount }]);
  const editAddRow = (idx, field, val) => setAddRecipients(r => r.map((x, i) => i === idx ? { ...x, [field]: val } : x));
  const removeAddRow = (idx) => setAddRecipients(r => r.filter((_, i) => i !== idx));
  const saveAddRecipients = async () => {
    const valid = addRecipients.filter(r => r.name && (r.email || r.phone) && Number(r.amount_brl) > 0);
    if (valid.length === 0) return message.warning('Adiciona pelo menos 1 destinatário válido (nome + email OU telefone + valor)');
    setAddSaving(true);
    try {
      // send_now=false: só cria como aguardando. Dono depois marca na tabela
      // e clica "Enviar selecionadas" — aí que debita crédito.
      const r = await api.post(`/community/finance/charges/batches/${id}/add-recipients`, {
        team_id: teamId,
        send_now: false,
        recipients: valid.map(x => ({
          name: x.name,
          email: x.email || null,
          phone: x.phone ? String(x.phone).replace(/\D/g, '') : null,
          // Se absorbFee: cobra do jogador o valor bruto pra dono receber
          // o líquido escolhido.
          amount_brl: addAbsorbFee
            ? round2(grossUp(x.amount_brl, addAssumedMethods))
            : Number(x.amount_brl),
        })),
      });
      message.success(`${r.data.added} destinatário(s) adicionado(s) como aguardando. Marca quais quer enviar e clica em "Enviar selecionadas".`);
      setAddOpen(false);
      load();
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou adicionar');
    } finally { setAddSaving(false); }
  };

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [sendingSelected, setSendingSelected] = useState(false);
  const CREDITS_PER_CHARGE = 1;
  const selectedCount = selectedRowKeys.length;
  const creditsToDebit = selectedCount * CREDITS_PER_CHARGE;

  const sendSelectedCharges = async () => {
    if (selectedRowKeys.length === 0) return message.warning('Marca pelo menos 1 cobrança');
    setSendingSelected(true);
    try {
      const r = await api.post(`/community/finance/charges/batches/${id}/send-selected`, {
        team_id: teamId,
        charge_ids: selectedRowKeys,
      });
      message.success(`${r.data.sent} cobrança(s) sendo enviada(s). Créditos gastos: ${r.data.credits_debited}.`);
      setSelectedRowKeys([]);
      setTimeout(load, 2500);
    } catch (e) {
      if (e.response?.status === 409) message.error(e.response.data.message || 'Créditos insuficientes');
      else message.error(e.response?.data?.message || 'Falhou enviar');
    } finally { setSendingSelected(false); }
  };
  const openEdit = () => {
    editForm.setFieldsValue({
      name: data?.batch?.name || '',
      // Descrição vem da primeira charge pending (todas do batch têm a mesma).
      description: (charges.find(c => ['pending', 'error'].includes(c.status)) || charges[0])?.description || '',
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      await api.put(`/community/finance/charges/batches/${id}`, {
        team_id: teamId,
        name: values.name,
        description: values.description,
      });
      message.success('Cobrança atualizada');
      setEditOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return; // Form validation
      message.error(e.response?.data?.message || 'Falhou atualizar');
    }
  };

  const charges = data?.charges || [];
  // Charges aguardando envio (pending sem mp_preference_id) — elegíveis pra
  // seleção manual via checkbox + botão "Enviar selecionadas".
  const sendableCharges = charges.filter(c =>
    (c.status === 'pending' || c.status === 'error') && !c.mp_preference_id
  );
  // Elegíveis pra reforço em massa: pending/overdue COM mp_preference_id
  // (só faz sentido reforçar cobrança que chegou no MP). Charges sem
  // mp_preference_id são as que falharam no envio — pra essas o botão
  // "Excluir" aparece na linha ou "Excluir lote" no header.
  const eligibleForRemind = charges.filter(c =>
    ['pending', 'overdue'].includes(c.status) && c.mp_preference_id
  );
  // Se TODAS as charges falharam (nenhuma tem mp_preference_id), pode
  // excluir o lote inteiro. Se pelo menos uma foi enviada, backend recusa.
  const canDeleteBatch = charges.length > 0 && charges.every(c => !c.mp_preference_id);

  const openBulkRemind = () => {
    setRemindTarget(null);
    setRemindChannels({ email: true, whatsapp: false });
    setRemindOpen(true);
  };
  const openSingleRemind = (chargeId) => {
    setRemindTarget(chargeId);
    setRemindChannels({ email: true, whatsapp: false });
    setRemindOpen(true);
  };

  const remindErrorMessage = (e) => {
    const status = e.response?.status;
    const backendMsg = e.response?.data?.message;
    const backendReason = e.response?.data?.reason;
    if (status === 402) return 'Créditos insuficientes. Compra mais créditos no card.';
    if (status === 429) return 'Você já mandou reforço nas últimas 24h dessa cobrança.';
    if (status === 503) return 'WhatsApp não configurado ainda. Fala com o suporte.';
    if (status === 403) return backendMsg || ACCESS_REASON_MSG[backendReason] || 'Sem acesso pra essa ação.';
    return backendMsg || 'Falhou mandar o reforço.';
  };

  const executeRemind = async () => {
    const { email, whatsapp } = remindChannels;
    if (!email && !whatsapp) { message.warning('Escolhe pelo menos um canal, sócio'); return; }
    setRemindLoading(true);
    try {
      if (remindTarget) {
        const r = await api.post(`/community/finance/charges/${remindTarget}/remind`, { channels: remindChannels });
        const sent = r.data.sent || {};
        const notas = [];
        if (email && sent.email === false) notas.push('email não saiu');
        if (whatsapp && sent.whatsapp === false) notas.push('WhatsApp não saiu');
        if (notas.length > 0) {
          message.warning(`Reforço parcial: ${notas.join(' e ')}. Créditos gastos: ${r.data.credits_debited || 0}`, 6);
        } else {
          message.success(`Reforço mandado! Créditos gastos: ${r.data.credits_debited || 0}`);
        }
      } else {
        const r = await api.post(`/community/finance/charges/batches/${id}/remind-all-pending`, { channels: remindChannels });
        const { processed, credits_debited, skipped_24h, errors } = r.data;
        if (errors && errors.length > 0) {
          const preview = errors.slice(0, 3).map(e => `#${e.charge_id}: ${e.error}`).join('; ');
          message.warning(`${processed} reforços mandados, ${errors.length} com problema (${preview}${errors.length > 3 ? '...' : ''}).`, 8);
        } else {
          message.success(`${processed} reforços mandados${skipped_24h ? `, ${skipped_24h} pulados (reforço recente)` : ''}. Créditos gastos: ${credits_debited || 0}`);
        }
      }
      setRemindOpen(false);
      load();
    } catch (e) {
      message.error(remindErrorMessage(e));
    } finally {
      setRemindLoading(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Carregando…</div>;
  if (!data) return null;
  const { batch, summary } = data;

  return (
    <div style={{ padding: '16px 12px', maxWidth: 1200, margin: '0 auto' }}>
      {isProcessing && (
        <Alert
          type="info"
          showIcon
          icon={<LoadingOutlined spin />}
          message="Processando cobranças…"
          description="Estamos enviando as cobranças pro Mercado Pago. A tela atualiza sozinha em segundos."
          style={{ marginBottom: 16 }}
          action={<SyncOutlined spin style={{ color: '#128C7E' }} />}
        />
      )}
      <Row justify="space-between" align="top" wrap gutter={[8, 12]}>
        <Col xs={24} md={12}>
          <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => nav('/comunidade/financeiro')}>Voltar</Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <Title level={4} style={{ margin: 0 }}>{batch.name}</Title>
            <Button type="text" size="small" icon={<EditOutlined />} onClick={openEdit} title="Editar nome/descrição">
              Editar
            </Button>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Criado {dayjs(batch.created_at).format('DD/MM HH:mm')} · {batch.sent_at ? `Enviado ${dayjs(batch.sent_at).format('DD/MM HH:mm')}` : 'Não enviado'} · <Tag>{batch.status}</Tag>
          </Text>
        </Col>
        <Col xs={24} md={12} style={{ textAlign: 'right' }}>
          <Space wrap>
            {canDeleteBatch && (
              <Popconfirm
                title="Excluir essa cobrança?"
                description={`Nenhum dos ${charges.length} destinatário(s) recebeu (todas deram erro no envio). Pode remover.`}
                okText="Sim, excluir"
                okButtonProps={{ danger: true }}
                cancelText="Cancelar"
                onConfirm={deleteBatch}
              >
                <Button danger type="primary" icon={<DeleteOutlined />}>
                  Excluir cobrança
                </Button>
              </Popconfirm>
            )}
            {/* Só permite adicionar em batches não-em-processamento */}
            {batch.status !== 'scheduled' && batch.status !== 'sending' && (
              <Button icon={<UserAddOutlined />} onClick={() => setAddOpen(true)}>
                Adicionar destinatários
              </Button>
            )}
            <Tooltip title={eligibleForRemind.length === 0 ? 'Nenhuma cobrança pendente pra reforçar por aqui' : ''}>
              <Button
                type="primary"
                icon={<WhatsAppOutlined />}
                style={eligibleForRemind.length === 0
                  ? { background: '#e5e5e5', borderColor: '#d9d9d9', color: '#8c8c8c' }
                  : { background: '#128C7E', borderColor: '#128C7E' }}
                onClick={openBulkRemind}
                disabled={eligibleForRemind.length === 0}
              >
                Cobrar devedores ({eligibleForRemind.length})
              </Button>
            </Tooltip>
          </Space>
        </Col>
      </Row>
      <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" styles={{ body: { padding: 12 } }}>
            <Statistic title="Total" value={summary.total} valueStyle={{ fontSize: 20 }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" styles={{ body: { padding: 12 } }}>
            <Statistic title="Pagas" value={summary.paid} valueStyle={{ color: '#128C7E', fontSize: 20 }} />
            <Text type="secondary" style={{ fontSize: 11 }}>{fmtBRL(summary.total_paid_amount_brl)}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" styles={{ body: { padding: 12 } }}>
            <Statistic title="Pendentes" value={summary.pending} valueStyle={{ color: '#faad14', fontSize: 20 }} />
            <Text type="secondary" style={{ fontSize: 11 }}>{fmtBRL(summary.total_pending_amount_brl)}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" styles={{ body: { padding: 12 } }}>
            <Statistic title="Vencidas" value={summary.overdue} valueStyle={{ color: '#dc2626', fontSize: 20 }} />
          </Card>
        </Col>
      </Row>
      {sendableCharges.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message={
            <Row justify="space-between" align="middle" wrap gutter={[8, 8]}>
              <Col>
                <Text strong>{sendableCharges.length} cobrança(s) aguardando envio</Text>
                {' · '}
                {selectedCount > 0
                  ? <Text>Selecionou {selectedCount} · vai debitar <Text strong>{creditsToDebit} crédito(s)</Text></Text>
                  : <Text type="secondary">Marque as que quer enviar na tabela abaixo</Text>}
              </Col>
              <Col>
                <Space wrap>
                  {selectedCount > 0 && (
                    <Button size="small" onClick={() => setSelectedRowKeys([])}>
                      Limpar seleção
                    </Button>
                  )}
                  <Button size="small" onClick={() => setSelectedRowKeys(sendableCharges.map(c => c.id))}>
                    Selecionar todas
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={selectedCount === 0
                      ? { background: '#e5e5e5', borderColor: '#d9d9d9', color: '#8c8c8c' }
                      : { background: '#128C7E', borderColor: '#128C7E' }}
                    disabled={selectedCount === 0}
                    loading={sendingSelected}
                    onClick={sendSelectedCharges}
                  >
                    Enviar selecionadas ({selectedCount})
                  </Button>
                </Space>
              </Col>
            </Row>
          }
        />
      )}
      <Card style={{ marginTop: 16 }} styles={{ body: { padding: 12 } }}>
        <div style={{ overflowX: 'auto' }}>
        <Table rowKey="id" size="small" dataSource={charges}
          scroll={{ x: 800 }}
          rowSelection={sendableCharges.length > 0 ? {
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            // Só permite selecionar cobranças que ainda não foram enviadas
            getCheckboxProps: (r) => ({
              disabled: !((r.status === 'pending' || r.status === 'error') && !r.mp_preference_id),
            }),
          } : undefined}
          columns={[
            { title: 'Nome', dataIndex: 'recipient_name', width: 140,
              render: v => <span style={{ whiteSpace: 'nowrap' }}>{v}</span> },
            { title: 'Email', dataIndex: 'recipient_email', width: 200,
              render: v => v ? <Text style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary">—</Text> },
            { title: 'Valor', dataIndex: 'amount_brl', width: 90, align: 'right',
              render: v => <span style={{ whiteSpace: 'nowrap' }}>{fmtBRL(v)}</span> },
            { title: 'Situação', dataIndex: 'status', width: 110, render: v => {
              const map = {
                pending: { color: 'orange', label: 'Aguardando' },
                paid: { color: 'green', label: 'Paga' },
                refunded: { color: 'red', label: 'Estornada' },
                cancelled: { color: 'default', label: 'Cancelada' },
                error: { color: 'red', label: 'Com erro' },
                overdue: { color: 'red', label: 'Vencida' },
              };
              const s = map[v] || { color: 'default', label: v };
              return <Tag color={s.color}>{s.label}</Tag>;
            } },
            { title: 'Pago em', dataIndex: 'paid_at', width: 90,
              render: v => v ? <span style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{dayjs(v).format('DD/MM HH:mm')}</span> : '—' },
            { title: 'Você recebe', key: 'net', width: 130, align: 'right', render: (_, r) => {
              if (r.status !== 'paid') return '—';
              const net = Number(r.net_to_organizer_brl || 0);
              const release = r.expected_release_at ? dayjs(r.expected_release_at) : null;
              const isFuture = release && release.isAfter(dayjs());
              return (
                <div style={{ whiteSpace: 'nowrap' }}>
                  <Text strong style={{ color: '#128C7E' }}>R$ {net.toFixed(2).replace('.', ',')}</Text>
                  {release && (
                    <div>
                      <Text style={{ fontSize: 10, color: isFuture ? '#faad14' : '#128C7E' }}>
                        {isFuture ? `Cai ${release.format('DD/MM')}` : '✓ Disponível'}
                      </Text>
                    </div>
                  )}
                </div>
              );
            } },
            { title: 'MP', dataIndex: 'mp_payment_id', width: 90,
              render: v => v ? <Text style={{ fontSize: 10 }}>#{v}</Text> : '—' },
            { title: 'Ações', key: 'act', render: (_, r) => {
              // "Com erro": pending sem mp_preference_id (createCharge falhou → nunca foi enviada).
              const withError = (r.status === 'pending' || r.status === 'error') && !r.mp_preference_id;
              const canEdit = r.status !== 'paid';
              return (
                <Space size="small">
                  {canEdit && (
                    <Button size="small" type="text" icon={<EditOutlined />}
                      onClick={() => openEditCharge(r)}
                      title="Editar nome, contato ou valor">
                      Editar
                    </Button>
                  )}
                  {['pending', 'overdue'].includes(r.status) && !withError && (
                    <Button size="small" icon={<WhatsAppOutlined />} onClick={() => openSingleRemind(r.id)}>
                      Reforçar
                    </Button>
                  )}
                  {['pending', 'overdue', 'error'].includes(r.status) && (
                    <Button size="small" icon={<CheckCircleOutlined />} onClick={() => openMarkPaid(r)}
                      title="Marcar como paga (dinheiro, pix externo, etc.)">
                      Marcar paga
                    </Button>
                  )}
                  {r.status === 'pending' && r.pay_url && (
                    <Button size="small" onClick={() => window.open(r.pay_url, '_blank')}>Ver link</Button>
                  )}
                  {/* Botão Estornar removido intencionalmente (M11) — funcionalidade
                      permanece no backend, mas UI não expõe. */}
                  {withError && (
                    <Popconfirm
                      title="Excluir essa cobrança?"
                      description="Ela não chegou no destinatário (deu erro no envio). Pode remover sem risco."
                      okText="Excluir"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteCharge(r.id)}
                    >
                      <Button danger size="small" icon={<DeleteOutlined />}>Excluir</Button>
                    </Popconfirm>
                  )}
                </Space>
              );
            } },
          ]}
          pagination={{ pageSize: 20, simple: true, size: 'small' }} />
        </div>
      </Card>

      <Modal
        open={remindOpen}
        title={remindTarget ? 'Reforçar cobrança' : `Cobrar devedores (${eligibleForRemind.length})`}
        onCancel={() => setRemindOpen(false)}
        confirmLoading={remindLoading}
        onOk={executeRemind}
        okText="Enviar"
        cancelText="Cancelar"
        width={520}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text>
            {remindTarget
              ? 'Escolhe por onde manda o reforço pra esse aí.'
              : 'Escolhe por onde manda o reforço pra galera que ainda não pagou.'}
          </Text>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Checkbox
              checked={remindChannels.email}
              onChange={e => setRemindChannels(c => ({ ...c, email: e.target.checked }))}
            >
              Email (grátis)
            </Checkbox>
            <Checkbox
              checked={remindChannels.whatsapp}
              onChange={e => setRemindChannels(c => ({ ...c, whatsapp: e.target.checked }))}
            >
              WhatsApp (1 crédito por mensagem)
            </Checkbox>
          </Space>
          {remindChannels.whatsapp && (
            <Alert
              type="info"
              showIcon
              message={remindTarget
                ? 'Vai debitar 1 crédito.'
                : `Vai debitar até ${eligibleForRemind.length} créditos (1 por WhatsApp enviado, sem contar quem já recebeu reforço nas últimas 24h).`
              }
            />
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            Reforço na mesma cobrança só de novo depois de 24h, pra não encher o saco de ninguém.
          </Text>
        </Space>
      </Modal>

      <Modal
        open={addOpen}
        title="Adicionar destinatários à cobrança"
        onCancel={() => setAddOpen(false)}
        onOk={saveAddRecipients}
        okText={`Adicionar ${addRecipients.filter(r => r.name && (r.email || r.phone) && Number(r.amount_brl) > 0).length}`}
        cancelText="Cancelar"
        confirmLoading={addSaving}
        okButtonProps={{ style: { background: '#128C7E', borderColor: '#128C7E' } }}
        destroyOnClose
        width={720}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message="Os destinatários entram como aguardando envio. Nada é debitado agora — depois você marca quais quer mandar e o crédito é debitado no envio."
          />
          <div>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Valor padrão (aplica ao adicionar linha):</Text>
            <InputNumber
              min={1}
              value={addAmount}
              onChange={setAddAmount}
              prefix="R$"
              decimalSeparator=","
              precision={2}
              style={{ width: 160 }}
            />
          </div>
          <div>
            <Checkbox checked={addAbsorbFee} onChange={e => setAddAbsorbFee(e.target.checked)}>
              <Text strong>Embutir taxa MP no valor cobrado</Text>
            </Checkbox>
            <div style={{ marginLeft: 24 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Marcado: jogador paga um pouco mais e você recebe exatamente o valor informado. Cálculo usa PIX (~1%) — se pagador escolher cartão (~5%), você recebe um pouco menos.
              </Text>
            </div>
          </div>
          <div>
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <Text strong>Destinatários ({addRecipients.length})</Text>
              <Button size="small" icon={<PlusOutlined />} onClick={addManualRow}>Adicionar linha</Button>
            </Row>
            {addRecipients.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', background: '#fafafa', borderRadius: 8, border: '1px dashed #d9d9d9' }}>
                <Text type="secondary">Clica "Adicionar linha" pra começar.</Text>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table
                  rowKey={(_, i) => i}
                  size="small"
                  dataSource={addRecipients}
                  pagination={false}
                  scroll={{ x: 600 }}
                  columns={[
                    { title: 'Nome', dataIndex: 'name', width: 140,
                      render: (v, _r, i) => <Input value={v} onChange={e => editAddRow(i, 'name', e.target.value)} placeholder="Nome completo" /> },
                    { title: 'Email', dataIndex: 'email', width: 200,
                      render: (v, _r, i) => <Input value={v} onChange={e => editAddRow(i, 'email', e.target.value)} placeholder="email@..." /> },
                    { title: 'Telefone', dataIndex: 'phone', width: 130,
                      render: (v, _r, i) => <Input value={v} onChange={e => editAddRow(i, 'phone', e.target.value)} placeholder="5511..." /> },
                    { title: addAbsorbFee ? 'Você recebe' : 'Valor', dataIndex: 'amount_brl', width: 110,
                      render: (v, _r, i) => <InputNumber min={1} value={v} onChange={x => editAddRow(i, 'amount_brl', x)} prefix="R$" decimalSeparator="," precision={2} style={{ width: '100%' }} /> },
                    ...(addAbsorbFee ? [{
                      title: 'Cobrado', width: 110, align: 'right',
                      render: (_v, r) => {
                        const gross = round2(grossUp(r.amount_brl || 0, addAssumedMethods));
                        return (
                          <Tooltip title="Valor cobrado do jogador (bruto). Inclui a taxa MP pra você receber o valor da coluna 'Você recebe'.">
                            <Text strong style={{ color: '#faad14' }}>R$ {gross.toFixed(2).replace('.', ',')}</Text>
                          </Tooltip>
                        );
                      },
                    }] : []),
                    { title: '', width: 40,
                      render: (_v, _r, i) => <Button danger size="small" type="text" icon={<DeleteOutlined />} onClick={() => removeAddRow(i)} /> },
                  ]}
                />
              </div>
            )}
          </div>
        </Space>
      </Modal>

      <Modal
        open={editOpen}
        title="Editar cobrança"
        onCancel={() => setEditOpen(false)}
        onOk={saveEdit}
        okText="Salvar"
        cancelText="Cancelar"
        okButtonProps={{ style: { background: '#128C7E', borderColor: '#128C7E' } }}
        destroyOnClose
        width={520}
      >
        <Form form={editForm} layout="vertical" preserve={false}>
          <Form.Item
            label="Nome do lote"
            name="name"
            rules={[
              { required: true, message: 'Coloca um nome' },
              { max: 200, message: 'Máximo 200 caracteres' },
            ]}>
            <Input placeholder="Ex: Mensalidade agosto" />
          </Form.Item>
          <Form.Item
            label="Descrição (opcional)"
            name="description"
            rules={[{ max: 500, message: 'Máximo 500 caracteres' }]}
            extra="Aparece pra quem recebe a cobrança. Alterar aqui atualiza só cobranças ainda pendentes — pagas ficam com o texto original."
          >
            <Input.TextArea rows={3} maxLength={500} showCount placeholder="Ex: Mensalidade referente ao mês de agosto" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Editar cobrança individual (por linha da tabela) */}
      <Modal
        open={editChargeOpen}
        title={`Editar cobrança${editingCharge?.recipient_name ? ` — ${editingCharge.recipient_name}` : ''}`}
        onCancel={() => { setEditChargeOpen(false); setEditingCharge(null); }}
        onOk={saveEditCharge}
        confirmLoading={editChargeSaving}
        okText="Salvar"
        cancelText="Cancelar"
        okButtonProps={{ style: { background: '#128C7E', borderColor: '#128C7E' } }}
        destroyOnClose
        width={520}
      >
        <Form form={editChargeForm} layout="vertical" preserve={false}>
          <Form.Item label="Nome" name="recipient_name"
            rules={[{ required: true, message: 'Nome é obrigatório' }, { max: 200 }]}>
            <Input placeholder="Nome do destinatário" />
          </Form.Item>
          <Form.Item label="Email" name="recipient_email"
            rules={[{ type: 'email', message: 'Email inválido', validateTrigger: 'onBlur' }]}
            extra="Precisa email OU telefone.">
            <Input placeholder="email@..." />
          </Form.Item>
          <Form.Item label="Telefone" name="recipient_phone">
            <Input placeholder="5511..." />
          </Form.Item>
          <Form.Item label="Valor (R$)" name="amount_brl"
            rules={[{ required: true, message: 'Valor é obrigatório' }]}
            extra={editingCharge?.mp_preference_id
              ? '⚠️ Link/Pix desta cobrança já foi gerado. Valor não pode mudar aqui — exclua e crie de novo se precisar corrigir o valor.'
              : 'Ainda dá pra editar o valor (link ainda não foi gerado).'}>
            <InputNumber
              min={0.01}
              disabled={!!editingCharge?.mp_preference_id}
              prefix="R$"
              decimalSeparator=","
              precision={2}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={markPaidOpen}
        title={<Space><CheckCircleOutlined style={{ color: '#52c41a' }} /> Marcar como paga</Space>}
        onCancel={() => setMarkPaidOpen(false)}
        onOk={executeMarkPaid}
        okText="Confirmar pagamento"
        confirmLoading={markPaidSaving}
        cancelText="Cancelar"
      >
        {markPaidCharge && (
          <>
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message="Pagamento recebido fora do sistema?"
              description="Use pra confirmar pagamento em dinheiro, pix externo, transferência, etc. A cobrança fica marcada como paga e sai da lista de pendentes." />
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Cobrança: <b>{markPaidCharge.recipient_name}</b> · valor original {fmtBRL(markPaidCharge.amount_brl)}
            </Text>
            <Form form={markPaidForm} layout="vertical">
              <Form.Item name="paid_amount_brl" label="Valor recebido (R$)"
                rules={[{ required: true, type: 'number', min: 0.01, message: 'Valor > 0' }]}>
                <InputNumber min={0.01} step={5} precision={2} style={{ width: '100%' }}
                  formatter={v => `R$ ${v}`} parser={v => v.replace(/[^\d.,]/g, '').replace(',', '.')} />
              </Form.Item>
              <Form.Item name="paid_method" label="Como recebeu?"
                rules={[{ required: true, message: 'Escolhe o método' }]}>
                <Select
                  options={[
                    { value: 'cash', label: 'Dinheiro' },
                    { value: 'external_pix', label: 'PIX (fora do sistema)' },
                    { value: 'transfer', label: 'Transferência bancária' },
                    { value: 'other', label: 'Outro' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="note" label="Observação (opcional)" style={{ marginTop: 12 }}>
                <Input.TextArea rows={2} maxLength={500} placeholder="Ex: pagou em dinheiro no dia do jogo" />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
}
